import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/catalyst/button";
import { Checkbox, CheckboxField } from "@/components/catalyst/checkbox";
import { Label } from "@/components/catalyst/fieldset";
import { CalendarIcon, CalendarDaysIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "use-intl";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const START_YEAR = 2023;
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const YEARS = Array.from(
  { length: CURRENT_YEAR - START_YEAR + 1 },
  (_, i) => START_YEAR + i,
);

function maxMonthForYear(year: number): number {
  return year >= CURRENT_YEAR ? CURRENT_MONTH : 12;
}

export type TimeRange = "monthly" | "yearly";

interface Props {
  year: number;
  month: number | null;
  timeRange: TimeRange;
  showDepartures: boolean;
  showDeliveries: boolean;
  showRoutes: boolean;
  onDateChange: (year: number, month: number | null) => void;
  onTimeRangeChange: (range: TimeRange) => void;
  onShowDeparturesChange: (show: boolean) => void;
  onShowDeliveriesChange: (show: boolean) => void;
  onShowRoutesChange: (show: boolean) => void;
}

export function RelocationFlowControls({
  year,
  month,
  timeRange,
  showDepartures,
  showDeliveries,
  showRoutes,
  onDateChange,
  onTimeRangeChange,
  onShowDeparturesChange,
  onShowDeliveriesChange,
  onShowRoutesChange,
}: Props) {
  const t = useTranslations("report");

  const maxMonth = maxMonthForYear(year);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Layer Toggles */}
        <div className="flex flex-wrap items-center gap-6">
          <CheckboxField>
            <Checkbox
              color="blue"
              checked={showDepartures}
              onChange={onShowDeparturesChange}
            />
            <Label className="flex cursor-pointer items-center gap-2">
              <span className="inline-block size-3 rounded-full bg-blue-600" />
              {t("departures")}
            </Label>
          </CheckboxField>
          <CheckboxField>
            <Checkbox
              color="emerald"
              checked={showDeliveries}
              onChange={onShowDeliveriesChange}
            />
            <Label className="flex cursor-pointer items-center gap-2">
              <span className="inline-block size-3 rounded-full bg-emerald-600" />
              {t("deliveries")}
            </Label>
          </CheckboxField>
          <CheckboxField>
            <Checkbox
              color="indigo"
              checked={showRoutes}
              onChange={onShowRoutesChange}
            />
            <Label className="flex cursor-pointer items-center gap-2">
              <span className="inline-block h-0.5 w-4 rounded bg-indigo-500" />
              {t("showRoutes")}
            </Label>
          </CheckboxField>
        </div>

        {/* Time Range Toggle */}
        <div className="flex gap-2">
          <Button
            color={timeRange === "monthly" ? "indigo" : "white"}
            onClick={() => {
              onTimeRangeChange("monthly");
              if (month === null) {
                onDateChange(year, new Date().getMonth() + 1);
              }
            }}
          >
            <CalendarIcon data-slot="icon" />
            {t("monthly")}
          </Button>
          <Button
            color={timeRange === "yearly" ? "indigo" : "white"}
            onClick={() => {
              onTimeRangeChange("yearly");
              onDateChange(year, null);
            }}
          >
            <CalendarDaysIcon data-slot="icon" />
            {t("yearly")}
          </Button>
        </div>

        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{t("year")}</span>
          <div className="flex gap-1">
            {YEARS.map((y) => (
              <Button
                key={y}
                color={year === y ? "indigo" : "white"}
                onClick={() =>
                  onDateChange(
                    y,
                    month === null ? null : Math.min(month, maxMonthForYear(y)),
                  )
                }
              >
                {y}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Month Slider */}
      <div
        className={cn(
          "space-y-3 transition-opacity",
          timeRange === "yearly" && "pointer-events-none opacity-40",
        )}
      >
        <div className="flex justify-between px-1 text-xs text-gray-500">
          {MONTHS.map((m, i) => (
            <span
              key={m}
              className={cn(
                "w-8 text-center transition-colors",
                i + 1 > maxMonth && "opacity-30",
                timeRange === "monthly" &&
                  month === i + 1 &&
                  "font-bold text-indigo-600",
              )}
            >
              {m}
            </span>
          ))}
        </div>
        <Slider
          value={[Math.min(month ?? CURRENT_MONTH, maxMonth)]}
          onValueChange={([newMonth]) =>
            onDateChange(year, Math.min(newMonth, maxMonth))
          }
          min={1}
          max={MONTHS.length}
          step={1}
          disabled={timeRange === "yearly"}
          className="cursor-grab active:cursor-grabbing"
        />
      </div>
    </div>
  );
}
